import React, { useState, useEffect, useRef } from 'react';
import {
  CircularProgress,
} from '@mui/material';
import { useMFA } from '../../contexts/MFAContext';
import { useAuth } from '../../contexts/AuthContext';
import { MFATypes } from '@flashbacktech/flashbackclient/api';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../contexts/ApiContext';
import {
  serializeAuthenticationCredential,
  toPublicKeyCredentialRequestOptions,
} from '../../utils/passkey';
import { debugAuthLog } from '../../utils/debugAuth';

interface PostLoginMFAVerificationProps {
  onVerificationSuccess: (verifyResponse?: any) => void;
}

const PostLoginMFAVerification: React.FC<PostLoginMFAVerificationProps> = ({
  onVerificationSuccess,
}) => {
  const { mfaState, verifyMFALogin, setupMFAMethod, clearError } = useMFA();
  const { authState, globalData, setAuthState, handleSignOut } = useAuth();
  const navigate = useNavigate();
  const { apiClient } = useApi();

  const [activeTab, setActiveTab] = useState(0);
  const [verificationCode, setVerificationCode] = useState('');
  const [email, setEmail] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [sendMagicLinkError, setSendMagicLinkError] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<MFATypes.MFAType | null>(null);
  const magicLinkAutoSendDoneRef = useRef(false);

  // Get enabled MFA methods from context - use user-level enabled methods from status
  const enabledMethods = mfaState.status?.enabledMethods || [];
  const primaryMethod = mfaState.status?.primaryMethod;
  const isEnforced = mfaState.status?.isEnforced || false;

  // ALL HOOKS MUST BE CALLED FIRST - BEFORE ANY CONDITIONAL LOGIC

  useEffect(() => {
    debugAuthLog('[PostLoginMFA] PostLoginMFAVerification mounted', { enabledMethods: enabledMethods?.length, primaryMethod });
    return () => { debugAuthLog('[PostLoginMFA] PostLoginMFAVerification unmounted'); };
  }, []);

  // 1. Method selection hook
  useEffect(() => {
    if (enabledMethods.length > 0) {
      // Pre-select primary method if available, otherwise first method
      const methodToSelect = primaryMethod || enabledMethods[0];
      setSelectedMethod(methodToSelect);

      // Set active tab based on selected method
      const methodIndex = enabledMethods.indexOf(methodToSelect);
      if (methodIndex !== -1) {
        setActiveTab(methodIndex);
      }
    }
  }, [enabledMethods, primaryMethod]);

  // 2. Navigation logic hook
  useEffect(() => {
    if (enabledMethods.length === 0 && isEnforced) {
      navigate('/profile?tab=2fa');
    } else if (enabledMethods.length === 0 && !isEnforced) {
      onVerificationSuccess();
    }
  }, [enabledMethods.length, isEnforced, navigate, onVerificationSuccess]);

  // 3. MFA state monitoring hook
  useEffect(() => {
    // Monitor MFA state changes
  }, [enabledMethods, isEnforced, mfaState.error, mfaState.isVerifying]);

  // 4. Auto-focus hook
  useEffect(() => {
    if (enabledMethods.length === 1) {
      setSelectedMethod(enabledMethods[0]);
      setActiveTab(0);

      // For single method users, focus on the verification input
      const inputElement = document.querySelector('input') as HTMLInputElement;
      if (inputElement) {
        inputElement.focus();
      }
    }
  }, [enabledMethods.length, selectedMethod]);

  // 5. Sync email from globalData.userProfile when available
  useEffect(() => {
    const profileEmail = globalData?.userProfile?.email?.trim() || '';
    if (profileEmail) setEmail(profileEmail);
  }, [globalData?.userProfile?.email]);

  // 6. Auto-send magic link when Magic Link tab is active and we have profile email (no user input)
  useEffect(() => {
    const profileEmail = globalData?.userProfile?.email?.trim() || '';
    if (
      selectedMethod === MFATypes.MFAType.MAGIC_LINK &&
      profileEmail &&
      !magicLinkSent &&
      !isSendingMagicLink &&
      !magicLinkAutoSendDoneRef.current
    ) {
      debugAuthLog('[PostLoginMFA] PostLoginMFAVerification: auto-sending magic link to', profileEmail);
      magicLinkAutoSendDoneRef.current = true;
      setEmail(profileEmail);
      handleMagicLinkVerification(profileEmail);
    }
  }, [selectedMethod, globalData?.userProfile?.email, magicLinkSent, isSendingMagicLink]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSelectedMethod(enabledMethods[newValue]);
    setVerificationCode('');
    const profileEmail = globalData?.userProfile?.email?.trim() || '';
    setEmail(profileEmail);
    if (enabledMethods[newValue] === MFATypes.MFAType.MAGIC_LINK) {
      magicLinkAutoSendDoneRef.current = false;
    }
    setMagicLinkSent(false);
    setSendMagicLinkError(null);
    clearError();
  };

  const handleGoogleAuthVerification = async () => {
    if (!verificationCode.trim() || !selectedMethod) return;

    setIsVerifying(true);
    try {
      const response = await verifyMFALogin({
        mfaType: selectedMethod,
        code: verificationCode,
      });

      if (response.success) {
        // Success - pass the response so the wrapper can upgrade the token
        onVerificationSuccess(response);
      } else {
        // Error will be handled by the MFA context
        // Clear the verification code on error
        setVerificationCode('');
        // Don't redirect - stay on MFA page to show error
      }
    } catch (error) {
      // Handle verification errors gracefully
      // Clear the verification code on error
      setVerificationCode('');
      // Don't redirect - stay on MFA page to show error
      // The error will be displayed by the MFA context error handling
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBackToLogin = () => {
    try {
      sessionStorage.removeItem('mfa_pending');
      sessionStorage.removeItem('mfa_verified_token');
      sessionStorage.removeItem('mfa_verified_via_magic_link');
    } catch {
      /* ignore storage errors */
    }
    // Hard redirect to login page — page reload clears all React state
    handleSignOut('/login');
  };

  const handleMagicLinkVerification = async (emailOverride?: string) => {
    const emailToUse = (emailOverride ?? email).trim();
    if (!emailToUse || !selectedMethod) return;

    setIsSendingMagicLink(true);
    setSendMagicLinkError(null);
    clearError();
    try {
      // Call directly without withTokenRefresh — we hold a temporary token that
      // cannot be refreshed; the backend endpoint now accepts it via authenticateMFA.
      const response = (await apiClient.sendMagicLink()) as { success?: boolean; error?: string; message?: string };
      if (response?.success !== false) {
        setMagicLinkSent(true);
      } else {
        setSendMagicLinkError(response?.error || response?.message || 'Failed to send magic link.');
      }
    } catch (error) {
      setSendMagicLinkError(
        error instanceof Error ? error.message : 'Failed to send magic link. Check your connection and try again.'
      );
    } finally {
      setIsSendingMagicLink(false);
    }
  };

  const handleMagicLinkClick = async (token: string) => {
    if (!selectedMethod) return;

    setIsVerifying(true);
    try {
      const response = await verifyMFALogin({
        mfaType: selectedMethod,
        token: token,
      });

      if (response.success) {
        // Success - pass the response so the wrapper can upgrade the token
        onVerificationSuccess(response);
      }
    } catch (error) {
      // Handle verification errors gracefully
      // The error will be displayed by the MFA context error handling
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePasskeyVerification = async () => {
    if (!selectedMethod) return;

    setIsVerifying(true);
    try {
      // Call directly without withTokenRefresh — we hold a temporary token that
      // cannot be refreshed; the backend endpoint now accepts it via authenticateMFA.
      const optionsResponse = await apiClient.getPasskeyAuthOptions();
      if (!optionsResponse?.success || !optionsResponse.data) {
        return;
      }

      const options = toPublicKeyCredentialRequestOptions(optionsResponse.data);
      const credential = await navigator.credentials.get({ publicKey: options });
      if (!credential) {
        return;
      }

      const serializedCredential = serializeAuthenticationCredential(credential as PublicKeyCredential);
      const response = await verifyMFALogin({
        mfaType: selectedMethod,
        credential: serializedCredential,
      });

      if (response.success) {
        onVerificationSuccess(response);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const getMethodName = (mfaType: MFATypes.MFAType) => {
    switch (mfaType) {
      case MFATypes.MFAType.GOOGLE_AUTH:
        return 'Google/MS Authenticator';
      case MFATypes.MFAType.MAGIC_LINK:
        return 'Magic Link';
      case MFATypes.MFAType.PASSKEY:
        return 'Passkey';
      default:
        return 'Unknown Method';
    }
  };

  // ALL HOOKS ARE NOW DEFINED ABOVE - NO MORE HOOKS HERE

  // Don't render if we need to redirect - AFTER ALL HOOKS
  if (enabledMethods.length === 0) {
    return null;
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #F8F9FB 0%, #EEF0F6 100%)', padding: '24px' }}>
      <div style={{ width: '100%', maxWidth: '440px' }}>
        {/* Card */}
        <div style={{ background: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ padding: '32px 32px 24px', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '14px', background: 'linear-gradient(135deg, #617BFF 0%, #8B5CF6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#181D27', margin: '0 0 6px', letterSpacing: '-0.01em' }}>
              Two-Factor Authentication
            </h2>
            <p style={{ fontSize: '14px', color: '#717680', margin: 0, lineHeight: 1.5 }}>
              Verify your identity to continue
            </p>
          </div>

          {/* Tabs (multiple methods) */}
          {enabledMethods.length > 1 && (
            <div style={{ display: 'flex', borderTop: '1px solid #F2F4F7', borderBottom: '1px solid #F2F4F7' }}>
              {enabledMethods.map((method, index) => (
                <button
                  key={method}
                  onClick={(e) => handleTabChange(e, index)}
                  style={{
                    flex: 1, padding: '12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    background: activeTab === index ? '#F8F9FB' : 'transparent', border: 'none', cursor: 'pointer',
                    borderBottom: activeTab === index ? '2px solid #617BFF' : '2px solid transparent',
                    color: activeTab === index ? '#617BFF' : '#717680', fontSize: '13px', fontWeight: 600,
                    transition: 'all 0.2s',
                  }}
                >
                  {method === MFATypes.MFAType.GOOGLE_AUTH && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
                  )}
                  {method === MFATypes.MFAType.MAGIC_LINK && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                  )}
                  {method === MFATypes.MFAType.PASSKEY && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>
                  )}
                  {getMethodName(method)}
                </button>
              ))}
            </div>
          )}

          {/* Error */}
          {mfaState.error && (
            <div style={{ margin: '16px 32px 0', padding: '12px 16px', borderRadius: '10px', background: '#FEF3F2', border: '1px solid #FECDCA', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
              <span style={{ fontSize: '13px', color: '#B42318', flex: 1 }}>{mfaState.error}</span>
              <button onClick={clearError} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D92D20', padding: 0, fontSize: '18px', lineHeight: 1 }}>×</button>
            </div>
          )}

          {/* Content */}
          <div style={{ padding: '24px 32px 32px' }}>
            {enabledMethods.map((method, index) => (
              <div key={method} style={{ display: activeTab === index ? 'block' : 'none' }}>
                {/* Google Auth */}
                {method === MFATypes.MFAType.GOOGLE_AUTH && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '8px', background: '#F0EDFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
                      </div>
                      <span style={{ fontSize: '15px', fontWeight: 600, color: '#181D27' }}>Authenticator App</span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#717680', margin: '0 0 20px', lineHeight: 1.5 }}>
                      Enter the 6-digit code from your authenticator app
                    </p>

                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#344054', marginBottom: '6px' }}>
                      Verification Code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={verificationCode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        setVerificationCode(val);
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter' && verificationCode.trim().length === 6) handleGoogleAuthVerification(); }}
                      placeholder="000 000"
                      style={{
                        width: '100%', padding: '12px 16px', fontSize: '20px', fontWeight: 600, letterSpacing: '0.3em',
                        textAlign: 'center', border: '1px solid #D0D5DD', borderRadius: '10px', outline: 'none',
                        color: '#181D27', background: '#FFFFFF', boxSizing: 'border-box',
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#617BFF'; e.target.style.boxShadow = '0 0 0 3px rgba(97, 123, 255, 0.12)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#D0D5DD'; e.target.style.boxShadow = 'none'; }}
                      autoFocus
                    />
                    <p style={{ fontSize: '12px', color: '#98A2B3', margin: '6px 0 20px', textAlign: 'center' }}>
                      Code refreshes every 30 seconds
                    </p>

                    <button
                      onClick={handleGoogleAuthVerification}
                      disabled={!verificationCode.trim() || verificationCode.length < 6 || isVerifying}
                      style={{
                        width: '100%', padding: '12px', fontSize: '14px', fontWeight: 600, color: '#FFFFFF',
                        background: (!verificationCode.trim() || verificationCode.length < 6 || isVerifying) ? '#B0BCFF' : '#617BFF',
                        border: 'none', borderRadius: '10px', cursor: (!verificationCode.trim() || verificationCode.length < 6 || isVerifying) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      }}
                      onMouseEnter={(e) => { if (verificationCode.trim() && verificationCode.length === 6 && !isVerifying) (e.target as HTMLElement).style.background = '#5A6FE8'; }}
                      onMouseLeave={(e) => { if (verificationCode.trim() && verificationCode.length === 6 && !isVerifying) (e.target as HTMLElement).style.background = '#617BFF'; }}
                    >
                      {isVerifying ? <CircularProgress size={18} sx={{ color: '#FFFFFF' }} /> : null}
                      {isVerifying ? 'Verifying...' : 'Verify & Continue'}
                    </button>
                  </div>
                )}

                {/* Magic Link */}
                {method === MFATypes.MFAType.MAGIC_LINK && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '8px', background: '#EFF8FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2E90FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                      </div>
                      <span style={{ fontSize: '15px', fontWeight: 600, color: '#181D27' }}>Magic Link</span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#717680', margin: '0 0 20px', lineHeight: 1.5 }}>
                      {email ? `We'll send a secure login link to ${email}` : "We'll send a secure login link to your email"}
                    </p>

                    {sendMagicLinkError && (
                      <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '10px', background: '#FEF3F2', border: '1px solid #FECDCA', fontSize: '13px', color: '#B42318' }}>
                        {sendMagicLinkError}
                      </div>
                    )}

                    {!magicLinkSent ? (
                      <div>
                        {isSendingMagicLink ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '16px' }}>
                            <CircularProgress size={18} sx={{ color: '#617BFF' }} />
                            <span style={{ fontSize: '14px', color: '#535862' }}>Sending magic link…</span>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleMagicLinkVerification()}
                            disabled={!email.trim() || isSendingMagicLink}
                            style={{
                              width: '100%', padding: '12px', fontSize: '14px', fontWeight: 600, color: '#FFFFFF',
                              background: (!email.trim() || isSendingMagicLink) ? '#B0BCFF' : '#617BFF',
                              border: 'none', borderRadius: '10px',
                              cursor: (!email.trim() || isSendingMagicLink) ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
                            }}
                          >
                            Send Magic Link
                          </button>
                        )}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ padding: '16px', borderRadius: '10px', background: '#ECFDF3', border: '1px solid #ABEFC6', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#079455" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                            <span style={{ fontSize: '14px', fontWeight: 600, color: '#067647' }}>Magic link sent!</span>
                          </div>
                          <p style={{ fontSize: '13px', color: '#067647', margin: '6px 0 0' }}>Check your email and click the link to continue.</p>
                        </div>
                        <p style={{ fontSize: '13px', color: '#717680', margin: '0 0 12px' }}>Didn't receive the email? Check your spam folder.</p>
                        <button
                          onClick={() => { setMagicLinkSent(false); setSendMagicLinkError(null); magicLinkAutoSendDoneRef.current = false; }}
                          style={{ padding: '8px 20px', fontSize: '13px', fontWeight: 600, color: '#617BFF', background: '#F0EDFF', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s' }}
                        >
                          Try Again
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Passkey */}
                {method === MFATypes.MFAType.PASSKEY && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '8px', background: '#FFF4ED', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E04F16" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>
                      </div>
                      <span style={{ fontSize: '15px', fontWeight: 600, color: '#181D27' }}>Passkey</span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#717680', margin: '0 0 16px', lineHeight: 1.5 }}>
                      Authenticate using your device biometrics or security key.
                    </p>
                    <div style={{ padding: '14px 16px', borderRadius: '10px', background: '#F9FAFB', border: '1px solid #EAECF0', marginBottom: '20px' }}>
                      <p style={{ fontSize: '13px', color: '#535862', margin: 0, lineHeight: 1.5 }}>
                        You may be prompted to use fingerprint, face recognition, or your device PIN.
                      </p>
                    </div>
                    <button
                      onClick={handlePasskeyVerification}
                      disabled={isVerifying}
                      style={{
                        width: '100%', padding: '12px', fontSize: '14px', fontWeight: 600, color: '#FFFFFF',
                        background: isVerifying ? '#B0BCFF' : '#617BFF',
                        border: 'none', borderRadius: '10px', cursor: isVerifying ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      }}
                    >
                      {isVerifying ? <CircularProgress size={18} sx={{ color: '#FFFFFF' }} /> : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>
                      )}
                      {isVerifying ? 'Authenticating...' : 'Authenticate with Passkey'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Back to login */}
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            onClick={handleBackToLogin}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: '#717680', transition: 'color 0.2s' }}
            onMouseEnter={(e) => { (e.target as HTMLElement).style.color = '#414651'; }}
            onMouseLeave={(e) => { (e.target as HTMLElement).style.color = '#717680'; }}
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostLoginMFAVerification;
